import React, { useState, useEffect, useMemo, useContext } from "react";
import { Link } from "react-router";
import { useParams } from "react-router";

import { AppContexts } from "../providers";
// import { TownForm } from "../components/forms/town.js";
import { OfficeForm } from "../components/forms/office.js";
import { ElectionForm } from "../components/forms/election.js";
import { Slideout } from "../components/slideout.js";
import { MunicipalityHistory } from "../components/history.js";
import { RequirementForm } from "../components/forms/requirements.js";
import {
  useTownOffices,
  useTownRequirements,
  useTowns,
  useCreateOffice,
  useCreateElection,
  useCreateRequirement,
  useMunicipality,
  useMunicipalityOffices,
  useMunicipalityWards,
  useMunicipalityElections,
  useMunicipalityCollections,
  useUpdateTown,
  useDeleteOffice,
} from "../api-hooks.js";

import { useCreateNote, useDeleteNote } from "../api/hooks/note.hooks.js";
import { useCreateSource, useDeleteSource } from "../api/hooks/source.hooks.js";
import { useToggleFlag } from "../api/hooks/utils.hooks.js";

import {
  useWards,
  useCreateWard,
  useDeleteWard,
} from "../api/hooks/municipality.hooks.js";

import {
  cleanDateString,
  arr,
  obj,
  confirm,
  showNotification,
  confirmDeleteThen,
} from "../utils.js";
import { updateMunicipality } from "../api.js";
import historyIcon from "../icons/history.svg";

const getCompletionStatusClass = (status) => {
  switch (status) {
    case "IN_PROGRESS":
      return "in-progress";
    case "DONE":
      return "done";
    default:
      return "";
  }
};

const getCompletionStatusLabel = (status) => {
  switch (status) {
    case "IN_PROGRESS":
      return "In Progress";
    case "DONE":
      return "Done";
    default:
      return "Not Started";
  }
};

const getNextCompletionStatus = (status) => {
  switch (status) {
    case "IN_PROGRESS":
      return "DONE";
    case "DONE":
      return null;
    default:
      return "IN_PROGRESS";
  }
};

const Clerk = ({ contact }) => {
  if (!contact) {
    return null;
  }

  return (
    <div>
      <div>
        <b className="label">
          {contact.title || "Contact"}:
        </b>
        {contact.name ? <span>{contact.name}</span> : <span className="none">None</span>}
      </div>
      <div>
        <div>
          <b className="label">Individual email:</b>
          {contact.email ? <a href={`mailto:${contact.email}`}>{contact.email}</a> : <span className="none">None</span>}
        </div>
        <div>
          <b className="label">Office email:</b>
          {contact.office_email ? <a href={`mailto:${contact.office_email}`}>{contact.office_email}</a> : <span className="none">None</span>}
        </div>
        <div>
          <b className="label">Phone:</b>
          {contact.phone ? <a href={`tel:${contact.phone}`}>{contact.phone}</a> : <span className="none">None</span>}
        </div>
      </div>
    </div>
  );
};

export const Towns = () => {
  const {
    data: towns,
    isLoading: townsLoading,
    refetch: refetchTowns,
  } = useTowns();

  const params = useParams();

  const [town, setTown] = useState(null);
  const [office, setOffice] = useState(false);
  const [election, setElection] = useState(false);
  const [source, setSource] = useState(null);
  const [note, setNote] = useState(null);
  const [ward, setWard] = useState(null);
  const [search, setSearch] = useState("");
  const [usesWards, setUsesWards] = useState(false);

  const [showHistory, setShowHistory] = useState(false);

  const { data: offices, refetch: refetchOffices } = useMunicipalityOffices(
    town?.id,
  );
  const { data: wards, refetch: refetchWards } = useMunicipalityWards(town?.id);
  const { data: elections, refetch: refetchElections } =
    useMunicipalityElections(town?.id);
  const { data: collections, refetch: refetchCollections } =
    useMunicipalityCollections(town?.id);

  useEffect(() => {
    if (!towns) {
      return setTown(null);
    }

    let { slug } = params;
    let town = towns.find((t) => {
      return t.slug === slug;
    });

    setTown(town);
  }, [towns, params]);

  useEffect(() => {
    if ((wards || []).length > 0) {
      setUsesWards(true);
    } else {
      setUsesWards(false);
    }
  }, [wards]);

  const { mutateAsync: saveOffice } = useCreateOffice();
  const { mutateAsync: deleteOffice } = useDeleteOffice();
  const { mutateAsync: saveElection } = useCreateElection();
  const { mutateAsync: saveNote } = useCreateNote();
  const { mutateAsync: deleteNote } = useDeleteNote();
  const { mutateAsync: saveSource } = useCreateSource();
  const { mutateAsync: toggleFlag } = useToggleFlag();
  const { mutateAsync: deleteSource } = useDeleteSource();
  const { mutateAsync: saveWard } = useCreateWard();
  const { mutateAsync: deleteWard } = useDeleteWard();
  const { mutateAsync: saveTown } = useUpdateTown();

  const removeElection = (e) => {
    console.log(e);
  };

  const removeOffice = (o) => {
    console.log(o);
  };

  const removeSeat = (s) => {
    console.log(s);
  };

  const seatNames = (e) => {
    return (e.terms || []).map((t) => {
      return t.seat?.name || "";
    });
  };

  const toggleWards = () => {
    if (wards.length > 0) {
      return;
    }

    setUsesWards(!usesWards);
  };

  const handleToggleFlag = async (flag) => {
    if (!town) {
      return;
    }

    let resp = await toggleFlag({
      item_id: town.id,
      item_type: "municipality",
      name: flag,
    });

    refetchTowns();
  };

  const usesTME = useMemo(() => {
    return !!(town?.flags || []).find(
      (f) => f.name === "uses_town_meeting_electors",
    );
  }, [town]);

  const filteredTowns = useMemo(() => {
    return (towns || []).filter((t) => {
      if (search.trim() === "") {
        return true;
      } else {
        return t.name.toLowerCase().startsWith(search.toLowerCase());
      }
    });
  }, [towns, search]);

  const contactForms = useMemo(() => {
    if (!town) {
      return null;
    }

    const forms = (town.contacts || []).filter((c) => c.contact_form && c.contact_form.trim() !== "").map((c) => c.contact_form);
    
    return forms.map((c, index) => <span key={index}><a href={c}>{c}</a>{index < forms.length - 1 ? ", " : ""}</span>);
  }, [town]);

  const sourceForm = () => {
    return (
      <form className="grid row">
        <div className="width-1-12">
          <span
            key="confirm"
            className="icon affirm clickable action left-aligned"
            data-uk-icon="check"
            onClick={() => {
              return saveSource({
                item_id: town.id,
                item_type: "municipality",
                source: source,
              }).then(() => {
                setSource(null);
                refetchCollections();
              });
            }}
          ></span>
          <span
            key="cancel"
            className="icon cancel clickable action left-aligned"
            data-uk-icon="close"
            onClick={() => {
              setSource(null);
            }}
          ></span>
        </div>
        <div className="input-wrapper width-6-12">
          <input
            type="text"
            value={source.summary}
            placeholder="Summary: Town Charter, 2024 Annual Report, etc..."
            onInput={(e) => setSource({ ...source, summary: e.target.value })}
          />
        </div>
        <div className="input-wrapper width-5-12">
          <input
            type="text"
            value={source.url}
            placeholder="URL"
            onInput={(e) => setSource({ ...source, url: e.target.value })}
          />
        </div>
      </form>
    );
  };

  const noteForm = () => {
    return (
      <form>
        <div className="note grid">
          <div className="width-1-12">
            <span
              key="confirm"
              className="icon affirm clickable action left-aligned"
              data-uk-icon="check"
              onClick={() => {
                return saveNote({
                  item_id: town.id,
                  item_type: "municipality",
                  note: note,
                }).then(() => {
                  setNote(null);
                  refetchCollections();
                });
              }}
            ></span>
            <span
              key="cancel"
              className="icon cancel clickable action left-aligned"
              data-uk-icon="close"
              onClick={() => {
                setNote(null);
              }}
            ></span>
          </div>
          <div className="input-wrapper width-8-12">
            <input
              type="text"
              value={note.summary}
              placeholder="Summary: I wasn't sure about.., The mayor's election is..."
              onInput={(e) => setNote({ ...note, summary: e.target.value })}
            />
          </div>
        </div>
        <div className="note grid row">
          <div className="width-1-12"></div>
          <div className="content input-wrapper width-8-12">
            <div>
              <textarea
                type="text"
                value={note.content}
                placeholder="Note: This election system was designed by a 12th century madman"
                rows="6"
                onInput={(e) => setNote({ ...note, content: e.target.value })}
              ></textarea>
            </div>
          </div>
        </div>
      </form>
    );
  };

  const wardForm = () => {
    return (
      <form>
        <div className="note grid">
          <div className="width-1-12">
            <span
              key="confirm"
              className="icon affirm clickable action left-aligned"
              data-uk-icon="check"
              onClick={() => {
                return saveWard({ ...ward, municipality_id: town.id }).then(
                  () => {
                    setWard(null);
                    refetchWards();
                  },
                );
              }}
            ></span>
            <span
              key="cancel"
              className="icon cancel clickable action left-aligned"
              data-uk-icon="close"
              onClick={() => {
                setWard(null);
              }}
            ></span>
          </div>
          <div className="input-wrapper width-8-12">
            <input
              type="text"
              value={ward.name}
              placeholder="Ward Name"
              onInput={(e) => setWard({ ...ward, name: e.target.value })}
            />
          </div>
        </div>
      </form>
    );
  };

  const authContext = useContext(AppContexts.AuthContext);
  const loadingAuth = authContext.loading;
  const authed = !!authContext.user;

  useEffect(() => {
    const root = document.getElementById("root");
    if (!authed) {
      root.classList.add("disable-clicks");
    } else {
      root.classList.remove("disable-clicks");
    }
  }, [authed]);

  return (
    <section id="towns" className="page">
      <div className="sidebar">
        {townsLoading ? (
          <p className="loading">Loading...</p>
        ) : (
          <>
            <div className="sidebar-header">
              {!loadingAuth && !authed && (
                <div className="not-authed">
                  <p>Please sign in or sign up to edit towns</p>
                </div>
              )}
              <form className="uk-search uk-search-default">
                <div className="input-wrapper">
                  <input
                    type="text"
                    className="uk-search-input"
                    onInput={(e) => {
                      setSearch(e.target.value);
                    }}
                    value={search}
                  />
                  <span
                    className="uk-search-icon-flip"
                    data-uk-search-icon
                  ></span>
                </div>
              </form>
            </div>
            <ul className="uk-list">
              {filteredTowns.map((t) => {
                return (
                  <li key={t.name}>
                    <span
                      className={`${getCompletionStatusClass(t.completionStatus)} icon clickable`}
                      uk-icon={
                        t.completionStatus === "IN_PROGRESS"
                          ? "icon: refresh"
                          : "icon: check"
                      }
                      uk-tooltip={`title: ${getCompletionStatusLabel(t.completionStatus)}`}
                      onClick={() => {
                        const update = {
                          ...t,
                          completionStatus: getNextCompletionStatus(
                            t.completionStatus,
                          ),
                        };
                        saveTown(update);
                      }}
                      key={`${t.name}-${t.completionStatus}`}
                    ></span>
                    <Link to={`/towns/ma/${t.slug}`}>{t.name}</Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      <div className="page-content">
        {!town && <h2>Select a town to the left to get started</h2>}
        {!!town && (
          <>
            <div className="uk-flex town-header">
              <h2>
                <a href={town?.website || "#"} target="_blank">
                  {town?.name || "Town Name"}
                </a>
              </h2>
              <div>
                <a
                  href="https://docs.google.com/forms/d/e/1FAIpQLSekaJ1HUhgOj8M8gf6WsTKzslsCQXw7R8wGQh-swqlHv1QSww/viewform"
                  target="_blank"
                >
                  Report a Bug
                </a>
              </div>
              <div>
                <span
                  uk-icon="icon: history; ratio: 1.5"
                  uk-tooltip="Show town edit history"
                  className="history-icon"
                  onClick={() => {
                    setShowHistory((prev) => !prev);
                  }}
                ></span>
              </div>
            </div>

            <div className="uk-width-1-1 bottom-spacing">
              <div><b className="label">Contact info last updated:</b> {town?.contact_info_last_updated ? new Date(town.contact_info_last_updated).toLocaleString() : <span className="none">3/5/25</span>}</div>
              <div><b className="label">Has the clerk's office provided us any info?</b> {town?.clerk_office_provided_info === null || town?.clerk_office_provided_info === undefined ? <span className="none">Unknown</span> : (town.clerk_office_provided_info ? "Yes" : "No")}</div>
            </div>


            {contactForms.length > 0 && (
              <div className="uk-width-1-1 bottom-spacing">
                <div><b className="label">Contact {contactForms.length > 1 ? "forms" : "form"}: </b>{contactForms}</div>
              </div>
            )}

            <div className="contacts bottom-spacing">
              {/* I want the contacts side by side so I'mma do a reduce to make an array of pairs, and then map through that */}
              {town.contacts?.reduce((acc, c, idx) => {
                if (idx % 2 === 0) {
                  acc[idx/2] = [c];
                } else {
                  acc[Math.floor(idx/2)].push(c);
                }

                return acc;
              }, []).map((pair, idx) => {
                return (
                  <React.Fragment key={idx}>
                    {pair.map((c) => (
                        <Clerk contact={c} key={c.id} />
                    ))}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="uk-width-1-1">
              <div className="input-wrapper">
                <input
                  type="checkbox"
                  checked={usesWards}
                  id="usesWards"
                  onChange={() => {
                    toggleWards();
                  }}
                />
                <label>This town uses wards or districts</label>
              </div>
            </div>

            <div className="uk-width-1-1">
              <div className="input-wrapper">
                <input
                  type="checkbox"
                  checked={usesTME}
                  id="usesTME"
                  onChange={() => {
                    handleToggleFlag("uses_town_meeting_electors");
                  }}
                />
                <label>This town uses town meeting electors</label>
              </div>
            </div>

            <section className="uk-width-1-1">
              <div className="section-header">
                <h2>Offices</h2>
                <span
                  className="icon right-aligned clickable"
                  data-uk-icon="plus-circle"
                  onClick={() => {
                    setOffice({
                      id: null,
                      title: "",
                      description: "",
                      salary: 0,
                      elected: true,
                      min_hours: 0,
                      max_hours: 0,
                      seat_count: 0,
                      seats: [],
                    });
                  }}
                ></span>
              </div>
              {!!offices && (
                <div className="grid-list offices uk-width-1-1">
                  <div className="grid header">
                    <div className="width-1-12"></div>
                    <div className="width-8-12">Office</div>
                    <div className="width-2-12">
                      Shared{" "}
                      <span
                        uk-icon="icon: info; ratio: 0.9"
                        uk-tooltip="This office is shared across municipalities, e.g. regional school board position"
                      ></span>
                    </div>
                    <div className="width-1-12"># Seats</div>
                  </div>
                  {offices
                    .filter((o) => o.elected)
                    .map((o) => (
                      <div className="grid row" key={o.id}>
                        <div className="width-1-12">
                          <span
                            className="icon clickable left-aligned"
                            uk-icon="pencil"
                            onClick={() => {
                              setOffice((prev) => (prev ? false : o));
                            }}
                          ></span>
                          <span
                            className="icon clickable left-aligned"
                            uk-icon="trash"
                            onClick={() => {
                              confirmDeleteThen(() =>
                                deleteOffice(o.id).then(() => {
                                  setOffice(false);
                                  refetchOffices();
                                  showNotification({
                                    message: `Deleted office successfully`,
                                  });
                                }),
                              );
                            }}
                          ></span>
                        </div>
                        <div className="width-8-12">{o.title}</div>
                        <div className="width-2-12">
                          {o.shared ? <span uk-icon="check"></span> : undefined}
                        </div>
                        <div className="width-1-12">
                          {(o.seats || []).length}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </section>

            {/*<section className="uk-width-1-1">
              <div className="section-header">
                <h2>Elections</h2>
                <span
                  className="icon right-aligned clickable"
                  data-uk-icon="plus-circle"
                  onClick={() => {
                    setElection({
                      id: null,
                      polling_date: null,
                      deadlines: [],
                      requirements: [],
                      forms: [],
                      terms: [],
                    });
                  }}
                ></span>
              </div>
              {!!elections && (
                <div>
                  <div className="grid header">
                    <div className="width-1-12"></div>
                    <div className="width-5-12">Name</div>
                    <div className="width-3-12">Type</div>
                    <div className="width-3-12">Date</div>
                  </div>

                  {elections.map((e) => {
                    return (
                      <div className="grid row">
                        <div className="width-1-12">
                          <span
                            uk-icon="pencil"
                            onClick={() => {
                              setElection((prev) => (prev ? false : e));
                            }}
                          ></span>
                        </div>
                        <div className="width-5-12">{seatNames(e)}</div>
                        <div className="width-3-12">{e.type ?? "-"}</div>
                        <div className="width-3-12">
                          {cleanDateString(e.polling_date) ?? "-"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>*/}

            {usesWards && (
              <section className="uk-width-1-1">
                <div className="section-header">
                  <h2>Wards/Districts</h2>
                  <span
                    className="icon right-aligned clickable"
                    data-uk-icon="plus-circle"
                    onClick={() => {
                      setWard({
                        id: null,
                        name: "",
                      });
                    }}
                  ></span>
                </div>

                <div>
                  {!!ward && !ward.id && wardForm()}

                  {(wards || []).map((w) => {
                    return w.id !== ward?.id ? (
                      <div>
                        <div className="note grid">
                          <div className="width-1-12">
                            <span
                              className="icon clickable left-aligned"
                              uk-icon="pencil"
                              onClick={() => {
                                setWard({ ...w });
                              }}
                            ></span>
                            <span
                              className="icon clickable left-aligned"
                              uk-icon="trash"
                              onClick={() => {
                                confirmDeleteThen(() =>
                                  deleteWard(w).then(() => {
                                    setWard(null);
                                    refetchWards();
                                  }),
                                );
                              }}
                            ></span>
                          </div>
                          <div className="summary width-11-12">{w.name}</div>
                        </div>
                      </div>
                    ) : (
                      wardForm()
                    );
                  })}
                </div>
              </section>
            )}

            <section className="uk-width-1-1">
              <div className="section-header">
                <h2>Sources</h2>
                <span
                  className="icon right-aligned clickable"
                  data-uk-icon="plus-circle"
                  onClick={() => {
                    setSource({
                      id: null,
                      summary: "",
                      url: "",
                    });
                  }}
                ></span>
              </div>

              <div>
                <div className="grid header">
                  <div className="width-1-12"></div>
                  <div className="width-6-12">Summary</div>
                  <div className="width-5-12">URL</div>
                </div>

                {!!source && !source.id && sourceForm()}

                {(collections?.sources || []).map((s) => {
                  return s.id !== source?.id ? (
                    <div className="source grid row">
                      <div className="width-1-12">
                        <span
                          className="icon clickable left-aligned"
                          uk-icon="pencil"
                          onClick={() => {
                            setSource({ ...s });
                          }}
                        ></span>
                        <span
                          className="icon clickable left-aligned"
                          uk-icon="trash"
                          onClick={() => {
                            confirmDeleteThen(() =>
                              deleteSource({
                                item_id: town.id,
                                item_type: "municipality",
                                source: { ...s },
                              }).then(() => {
                                setSource(null);
                                refetchCollections();
                              }),
                            );
                          }}
                        ></span>
                      </div>
                      <div className="width-6-12">{s.summary}</div>
                      <div className="width-5-12 url">
                        <a href={s.url} target="_blank">
                          {s.url}
                        </a>
                      </div>
                    </div>
                  ) : (
                    sourceForm()
                  );
                })}
              </div>
            </section>

            <section className="uk-width-1-1">
              <div className="section-header">
                <h2>Notes</h2>
                <span
                  className="icon right-aligned clickable"
                  data-uk-icon="plus-circle"
                  onClick={() => {
                    setNote({
                      id: null,
                      summary: "",
                      content: "",
                    });
                  }}
                ></span>
              </div>

              <div>
                {!!note && !note.id && noteForm()}

                {(collections?.notes || []).map((n) => {
                  return n.id !== note?.id ? (
                    <div>
                      <div className="note grid">
                        <div className="width-1-12">
                          <span
                            className="icon clickable left-aligned"
                            uk-icon="pencil"
                            onClick={() => {
                              setNote({ ...n });
                            }}
                          ></span>
                          <span
                            className="icon clickable left-aligned"
                            uk-icon="trash"
                            onClick={() => {
                              confirmDeleteThen(() =>
                                deleteNote({
                                  item_id: town.id,
                                  item_type: "municipality",
                                  note: { ...n },
                                }).then(() => {
                                  setNote(null);
                                  refetchCollections();
                                }),
                              );
                            }}
                          ></span>
                        </div>
                        <div className="summary width-11-12">{n.summary}</div>
                      </div>
                      <div className="note grid row">
                        <div className="width-1-12"></div>
                        <div className="content width-8-12">
                          <div>{n.content}</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    noteForm()
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>
      <Slideout active={office} setActive={setOffice}>
        <form>
          <OfficeForm
            municipality={town}
            wards={wards || []}
            selected={office}
            onSave={async (o) => {
              return saveOffice({ municipality_id: town.id, office: o }).then(
                async (resp) => {
                  if (!resp.error_code) {
                    await refetchOffices();
                  }

                  return resp;
                },
              );
            }}
            onCancel={() => {
              setOffice(false);
            }}
          />
        </form>
      </Slideout>
      <Slideout active={election} setActive={setElection}>
        <form>
          <ElectionForm
            municipality={town}
            selected={election}
            onSave={(e) => {
              return saveElection({
                municipality_id: town.id,
                election: e,
              }).then(() => {
                return Promise.all([refetchElections(), refetchCollections()]);
              });
            }}
            onCancel={() => {
              setElection(false);
            }}
          />
        </form>
      </Slideout>
      <Slideout active={showHistory} setActive={setShowHistory}>
        <MunicipalityHistory
          municipality={town}
          close={() => setShowHistory(false)}
        />
      </Slideout>
    </section>
  );
};
